import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import * as Icons from "lucide-react";
import { ArrowRight, Download, CheckCircle2 } from "lucide-react";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import MegaMenuProductLink from "./MegaMenuProductLink";
import { productMenuCatalogueVisual, productMenuHelpVisual, visualForProductCategory } from "@/data/productMenuVisualData";
import "./mega-menu.css";

const BLUE = "#1D4ED8";
const NAVY = "#0B1F44";

export default function ProductsMegaMenu({ data, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose?.(); }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [onClose]);

  if (!data) return null;
  const categories = data.categories || [];
  const help = data.help_card || {};
  const cat = data.catalogue_card || {};

  return (
    <nav aria-label="Products" data-testid="products-mega-menu" className="absolute left-1/2 top-full z-50 -translate-x-1/2 px-4 pt-2">
      <div
        ref={ref}
        role="menu"
        className="mm-drop w-[min(1220px,calc(100vw-32px))] overflow-hidden rounded-[18px] border border-[#E4E9F2] bg-white shadow-[0_30px_70px_-30px_rgba(7,27,58,0.5)]"
      >
        {/*
          gridTemplateColumns is computed from the actual category count
          (not a hardcoded 5; columns may shrink to 0 min so the row never overflows at 1024 px) so a 6th, 7th, etc. category configured in
          Admin → Mega Menu always renders instead of being silently dropped
          by a fixed 12-column layout that only had room for exactly 5 slots
          + 1 reserved sidebar column. The sidebar (Help + Catalogue) keeps a
          fixed 240px column regardless of how many categories there are.
        */}
        <div
          className="grid gap-4 p-5 xl:gap-5 xl:p-7"
          style={{ gridTemplateColumns: `repeat(${Math.max(categories.length, 1)}, minmax(0, 1fr)) minmax(240px, 270px)` }}
        >
        {categories.map((c, idx) => {
          const Icon = (Icons[c.icon] || Icons.Settings);
          return (
            <div key={idx} className="min-w-0" data-testid={`mm-cat-${idx}`}>
              <div className="mb-3 flex items-start gap-2.5">
                <div className="h-9 w-9 shrink-0 rounded-full border border-[#1D4ED8]/30 bg-[#F5F8FF] text-[#1D4ED8] flex items-center justify-center">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div className="min-h-[2.7em] font-heading text-[12.5px] font-extrabold leading-[1.2] tracking-[0.12em] text-[#0B1F44] uppercase pt-1.5">
                  {c.title}
                </div>
              </div>

              <ProductTile category={c} onClose={onClose} />

              {c.description && (
                <p className="mb-4 text-[12px] leading-relaxed text-slate-500">{c.description}</p>
              )}

              <ul className="mb-5 space-y-1" aria-label={`${c.title} products`}>
                {(c.links || []).map((l, i) => (
                  <MegaMenuProductLink key={i} link={l} index={i} onClick={onClose} testId={`mm-cat-${idx}-link-${i}`} />
                ))}
              </ul>

              <Link
                to={c.slug ? `/products/${c.slug}` : (c.cta_url || "/products")}
                onClick={onClose}
                data-testid={`mm-cat-${idx}-cta`}
                className="inline-flex items-center gap-1.5 text-[10.5px] font-heading font-bold uppercase tracking-widest text-[#1D4ED8] hover:gap-2.5 transition-all border-t border-slate-100 pt-3 mt-1"
              >
                {c.cta_label || "View all"} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          );
        })}

        {/* Help + catalogue column — occupies the fixed trailing 240px
            column from gridTemplateColumns above regardless of category
            count. */}
        <div className="min-w-0 space-y-4">
          <div className="overflow-hidden rounded-lg text-white" style={{ backgroundColor: NAVY, minWidth: 0 }} data-testid="mm-help-card">
            {/* real application photo (technician cleaning a tube sheet), fading into the card */}
            <div className="relative h-[92px]">
              <img
                src={productMenuHelpVisual.src}
                alt={productMenuHelpVisual.alt}
                width={productMenuHelpVisual.width}
                height={productMenuHelpVisual.height}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover object-[50%_35%]"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(11,31,68,0.15)_0%,rgba(11,31,68,0.55)_60%,#0B1F44_100%)]" aria-hidden="true" />
            </div>
            <div className="p-5 pt-3">
            <div className="font-heading font-extrabold text-[15px] leading-tight mb-2">{help.title}</div>
            <div className="text-[11.5px] text-white/70 leading-relaxed mb-4">{help.description}</div>
            <ul className="space-y-2 mb-5">
              {(help.features || []).map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-[11.5px] text-white/85">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#3B82F6]" /> {f}
                </li>
              ))}
            </ul>
            <Link
              to={help.cta_url || "/contact"}
              onClick={onClose}
              data-testid="mm-help-cta"
              className="inline-flex items-center gap-1.5 border border-white/60 hover:bg-white hover:text-[#0B1F44] px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-widest transition-colors"
            >
              {help.cta_label || "Talk To An Engineer"} <ArrowRight className="w-3 h-3" />
            </Link>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4 flex gap-3.5" data-testid="mm-catalogue-card">
            {/* Admin → Mega Menu → Cover Image is the source of truth; the real catalogue cover here is only the
                fallback for a doc that has never had a cover set in Admin. */}
            <div className="w-[72px] shrink-0 overflow-hidden rounded-[3px] shadow-[0_8px_18px_-8px_rgba(11,31,68,0.5)] ring-1 ring-slate-200">
              {cat.image ? (
                <img src={toAbsoluteUrl(cat.image)} alt={cat.title} loading="eager" decoding="async" className="block h-auto w-full" />
              ) : (
                <img
                  src={productMenuCatalogueVisual.src}
                  alt={productMenuCatalogueVisual.alt}
                  width={productMenuCatalogueVisual.width}
                  height={productMenuCatalogueVisual.height}
                  loading="eager"
                  decoding="async"
                  className="block h-auto w-full"
                />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-[12px] text-[#0B1F44] leading-tight mb-1">{cat.title}</div>
              <div className="text-[10.5px] text-slate-500 leading-tight mb-2 line-clamp-2">{cat.description}</div>
              <Link
                to={cat.cta_url || "/resources"}
                onClick={onClose}
                data-testid="mm-catalogue-cta"
                className="inline-flex items-center gap-1.5 text-[10px] font-heading font-bold uppercase tracking-widest text-[#1D4ED8]"
              >
                {cat.cta_label || "Download Now"} <Download className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
        </div>
      </div>
    </nav>
  );
}


/**
 * Product photograph for one column. Fixed-height tile (150 px; 132 px below 1280) with the whole machine visible
 * (object-contain, never cover) on a light tile. Admin → Mega Menu → Category Image is the source of truth — changing
 * it there must change this tile; the pre-cropped cutout in src/data/productMenuVisualData.js (by category slug) is
 * only a fallback for a category that has never had an image set in Admin. Hover: image +3 % over 400 ms, tile turns
 * light blue with a Shingare-blue hairline.
 */
function ProductTile({ category: c, onClose }) {
  const adminImage = c.image ? toAbsoluteUrl(c.image) : null;
  const v = adminImage ? null : visualForProductCategory(c.slug);
  const img = v?.image;
  const fallback = !img ? adminImage : null;
  if (!img && !fallback) return null;
  const to = c.slug ? `/products/${c.slug}` : c.cta_url || "/products";
  // rendered width is bounded by the tile (~155 px) or by the 90 %-of-height limit — tell the browser, so it never over-fetches
  const shownW = img ? Math.round(Math.min(150, (132 * img.width) / img.height)) : 150;
  return (
    <Link
      to={to}
      onClick={onClose}
      aria-label={`${c.title} — view products`}
      data-testid={`mm-cat-tile-${c.slug || ""}`}
      className="group/tile mb-3 flex h-[104px] w-full items-center justify-center overflow-hidden rounded-[10px] border border-transparent bg-[#F5F7FA] transition-colors duration-300 hover:border-[#1D4ED8]/30 hover:bg-[#EEF4FF] focus-visible:border-[#1D4ED8]/40 focus-visible:bg-[#EEF4FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D4ED8] xl:h-[118px]"
    >
      {img ? (
        <img
          src={img.src}
          srcSet={img.srcSet}
          sizes={`${shownW}px`}
          alt={v.alt}
          width={img.width}
          height={img.height}
          loading="eager"
          decoding="async"
          draggable="false"
          className="h-auto max-h-[90%] w-auto max-w-[92%] object-contain transition-transform [transition-duration:400ms] ease-out [filter:drop-shadow(0_10px_10px_rgba(11,35,72,0.16))] group-hover/tile:scale-[1.03] group-focus-visible/tile:scale-[1.03]"
        />
      ) : (
        <img src={fallback} alt={c.title} loading="lazy" decoding="async" className="max-h-[90%] max-w-[92%] object-contain transition-transform [transition-duration:400ms] ease-out group-hover/tile:scale-[1.03]" />
      )}
    </Link>
  );
}
